import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  full_name: "",
  work_email: "",
  company: "",
  focus: "",
  message: "",
};

export default function InquiryModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle");
  const nameRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    nameRef.current?.focus();
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = ({ target }) => {
    setForm((current) => ({ ...current, [target.name]: target.value }));
    if (status !== "idle") setStatus("idle");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("submitting");
    try {
      await axios.post(`${API}/conversations`, form);
      setStatus("success");
      setForm(EMPTY_FORM);
      toast.success("Conversation request received");
    } catch (error) {
      setStatus("error");
      toast.error("We could not send that yet. Please try again.");
    }
  };

  return (
    <div
      className="inquiry-modal"
      data-testid="inquiry-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inquiry-title"
    >
      <button
        className="inquiry-modal__backdrop"
        type="button"
        aria-label="Close conversation form"
        data-testid="inquiry-backdrop-close"
        onClick={onClose}
      />
      <div className="inquiry-modal__panel">
        <div className="inquiry-modal__topline">
          <span className="eyebrow">ZUMAIA / STRATEGIC ACCESS</span>
          <button
            className="icon-button"
            type="button"
            aria-label="Close conversation form"
            data-testid="inquiry-close-button"
            onClick={onClose}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div className="inquiry-modal__intro">
          <h2 id="inquiry-title">Tell us what you&apos;re building.</h2>
          <p>
            Give us the signal. We&apos;ll come back with the people, structure, and
            next move to make it real.
          </p>
        </div>
        <form
          className="inquiry-form"
          data-testid="strategic-conversation-form"
          onSubmit={handleSubmit}
        >
          <label className="field">
            <span>Full name</span>
            <input
              ref={nameRef}
              data-testid="conversation-name-input"
              name="full_name"
              value={form.full_name}
              onChange={handleChange}
              autoComplete="name"
              required
            />
          </label>
          <label className="field">
            <span>Work email</span>
            <input
              data-testid="conversation-email-input"
              name="work_email"
              type="email"
              value={form.work_email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </label>
          <label className="field">
            <span>Company</span>
            <input
              data-testid="conversation-company-input"
              name="company"
              value={form.company}
              onChange={handleChange}
              autoComplete="organization"
              required
            />
          </label>
          <label className="field">
            <span>What is in motion?</span>
            <select
              data-testid="conversation-focus-select"
              name="focus"
              value={form.focus}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                Select a focus
              </option>
              <option value="Executive and leadership acquisition">
                Executive and leadership acquisition
              </option>
              <option value="People and company scaling">
                People and company scaling
              </option>
              <option value="Strategic team development">
                Strategic team development
              </option>
              <option value="Growth and capital planning">
                Growth and capital planning
              </option>
            </select>
          </label>
          <label className="field field--wide">
            <span>Briefly describe the next chapter</span>
            <textarea
              data-testid="conversation-message-input"
              name="message"
              value={form.message}
              onChange={handleChange}
              rows="4"
              required
            />
          </label>
          <div className="inquiry-form__footer">
            <p className="form-note">
              United States / Switzerland / Pre-Seed to Pre-IPO
            </p>
            <button
              className="button button--gold"
              data-testid="conversation-submit-button"
              type="submit"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? "SENDING…" : "START A CONVERSATION"}
              <ArrowUpRight size={17} strokeWidth={1.5} />
            </button>
          </div>
          <div
            className={`form-status form-status--${status}`}
            data-testid="conversation-form-status"
            aria-live="polite"
          >
            {status === "success" && "Your signal is in. We will be in touch."}
            {status === "error" && "Something interrupted the request. Please try again."}
          </div>
        </form>
      </div>
    </div>
  );
}
